/**
 * Mensagens e helpers padrão para os dados pessoais (nome, CPF e data de
 * nascimento).
 *
 * Nesta plataforma esses dados são **somente leitura**: toda alteração é feita
 * na Gestão de cadastros da congregação e chega aqui pela sincronização
 * automática. Nome é editável livremente lá; CPF e data de nascimento apenas
 * por administradores da Gestão.
 */
import { toast } from "sonner";

/** Plataforma onde os dados pessoais são realmente editados. */
export const GESTAO_URL = "https://cadastro.rechovot.org.br";

/** Rótulo do link para a Gestão de cadastros. */
export const GESTAO_LINK_LABEL = "Abrir a Gestão de cadastros";

/** Mensagem principal exibida ao tentar alterar dados pessoais aqui. */
export const SENSITIVE_FIELDS_SUPPORT_MESSAGE =
  "Os dados pessoais (nome, CPF e data de nascimento) não podem ser alterados nesta plataforma. A alteração é feita na Gestão de cadastros da congregação.";

/** Orientação complementar (descrição do toast / linha de apoio). */
export const SENSITIVE_FIELDS_SUPPORT_HINT =
  "Nome pode ser editado livremente na Gestão de cadastros; CPF e data de nascimento somente por administradores.";

/** Texto do link para a página de suporte. */
export const SENSITIVE_FIELDS_SUPPORT_LINK_LABEL = "Falar com o suporte";

/** Aviso informativo (não é erro) usado junto aos campos bloqueados. */
export const SENSITIVE_FIELDS_LOCKED_NOTICE =
  "Nome, CPF e data de nascimento são exibidos aqui apenas para consulta e ficam sempre iguais aos da Gestão de cadastros. Para alterá-los, use a Gestão de cadastros.";

/** Aviso exibido na ficha administrativa desta plataforma. */
export const SENSITIVE_FIELDS_ADMIN_NOTICE =
  "Nome, CPF e data de nascimento não são editáveis nesta plataforma, nem por administradores. Faça a correção na Gestão de cadastros: a sincronização automática atualiza os dados aqui.";

/** Indica se o erro veio de uma tentativa sem permissão em campos protegidos. */
export function isSensitiveFieldPermissionError(error: unknown): boolean {
  const err = error as { message?: string; code?: string } | null | undefined;
  const message = (err?.message ?? "").toLowerCase();
  const code = err?.code ?? "";
  if (code === "42501") return true;
  return (
    message.includes("insufficient_privilege") ||
    message.includes("insufficient privilege") ||
    message.includes("permission denied for column") ||
    message.includes(SENSITIVE_FIELDS_SUPPORT_MESSAGE.toLowerCase()) ||
    (message.includes("permission denied") &&
      (message.includes("cpf") || message.includes("birth_date")))
  );
}

/** Mensagem final a exibir na UI para um erro de salvamento. */
export function describeProfileSaveError(error: unknown, fallback: string): string {
  if (isSensitiveFieldPermissionError(error)) return SENSITIVE_FIELDS_SUPPORT_MESSAGE;
  const message = (error as { message?: string } | null | undefined)?.message;
  return message && message.trim() ? message : fallback;
}

/** Toast padronizado para erros de salvamento de perfil/ficha. */
export function toastProfileSaveError(error: unknown, fallback: string): string {
  const message = describeProfileSaveError(error, fallback);
  toast.error(message, {
    description: isSensitiveFieldPermissionError(error) ? SENSITIVE_FIELDS_SUPPORT_HINT : undefined,
  });
  return message;
}

/** Toast padronizado ao tentar digitar em um campo bloqueado. */
export function toastSensitiveFieldLocked() {
  toast.error(SENSITIVE_FIELDS_SUPPORT_MESSAGE, {
    id: "campos-sensiveis-bloqueados",
    description: SENSITIVE_FIELDS_SUPPORT_HINT,
  });
}
